/**
 * Runtime config, loaded from /config.json at app start (not baked in at build time).
 *
 * Why: this image is built ONCE per commit (content-hash reuse, buildComponent()) and PROMOTED
 * dev -> staging -> prod unchanged (delivery.md's "nothing is rebuilt for a higher environment").
 * Anything that differs per environment (API base path, auth settings, environment name) has to
 * come from outside the bundle. web/Dockerfile's entrypoint (docker-entrypoint.sh) writes this
 * file from environment variables the Helm chart sets per environment — see values.yaml /
 * values-dev.yaml / values-staging.yaml's `env:` block and README.md's "runtime config contract"
 * for the exact keys.
 *
 * Pini: replace every build-time-hardcoded value in providers.tsx (Auth0 domain/clientId/
 * audience, API_BASE_URL) with a read through this module instead. No Auth0 application exists
 * for this client yet (platform decision, 2026-10-01) — the keys below are a generic pass-through
 * contract so whatever auth config eventually lands slots in without a pipeline change.
 */
interface RuntimeConfig {
  /** Same-origin base path for API calls, e.g. "/api". Empty string also means same-origin. */
  apiBasePath: string;
  /** Which environment this is: "dev" | "staging" | "preview" | "prod". For display and telemetry,
   * and to hide what prod lacks (the feedback voice note, `server-support.ts`). */
  environment: string;
  /** The commit this web image was built from (VERSIONVAR): baked into the image at build time
   * (APP_COMMIT), so a deploy of the same image can never change it. "unknown" if the build did
   * not say; empty only when there is no /config.json at all. The release version and the
   * commits each component should be at are NOT here: they change without a restart, see
   * `lib/release-info.ts`. */
  commit: string;
  /**
   * Opaque pass-through for whatever auth settings the client ends up needing (Auth0 domain/
   * clientId/audience or otherwise) — kept as a free-form record rather than named fields so this
   * contract doesn't assume a specific identity provider. Populated from AUTH_CONFIG_JSON (see
   * values.yaml) if set, otherwise {}.
   */
  auth: Record<string, string>;
  /**
   * Elastic APM RUM (web-client-rum-and-replay, 2026-10-05). Empty serverUrl means RUM is OFF —
   * local dev and any environment that never sets RUM_SERVER_URL (today: prod, which is out of
   * scope for this change) get no agent at all, same fail-safe shape as `auth`. serviceVersion
   * is the build commit (RUM_SERVICE_VERSION, set per-deploy by the Jenkinsfile, not baked into
   * the image — see this image's "build once, promote unchanged" doc above). `strict` (RUM_STRICT)
   * turns on the privacy rules for production traces; `environment: "prod"` turns them on
   * whatever this says (`isStrict` in app/rum.ts).
   */
  rum: { serverUrl: string; serviceVersion: string; strict?: boolean };
  /** Set only by the Vite dev server (`vite.config.ts`), never in a deployed build. */
  devSource?: DevSource;
}

/** Which `web-api` a laptop dev server proxies to: dev's (built from `main`), a PR preview's,
 * or this checkout's on the laptop. `branch` is the laptop's checked-out branch. */
export interface DevSource {
  api: "dev" | "pr" | "local" | "staging";
  pr: number | null;
  branch: string;
  target: string;
}

const FALLBACK: RuntimeConfig = {
  apiBasePath: "/api",
  environment: "unknown",
  commit: "",
  auth: {},
  rum: { serverUrl: "", serviceVersion: "" },
};

let cached: RuntimeConfig | null = null;

/** The loaded config, for code that runs after start-up (`main.tsx` awaits `loadConfig()`
 * before the app's modules load). The defaults before that, and in tests. */
export function runtimeConfig(): RuntimeConfig {
  return cached ?? FALLBACK;
}

export async function loadConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  let resolved: RuntimeConfig;
  try {
    const res = await fetch("/config.json", { cache: "no-store" });
    if (!res.ok) throw new Error(`config.json: HTTP ${res.status}`);
    resolved = { ...FALLBACK, ...(await res.json()) };
  } catch (err) {
    // Local dev (vite dev server) has no entrypoint script writing /config.json — fall back
    // quietly rather than crash the app.
    console.warn("Falling back to default runtime config:", err);
    resolved = FALLBACK;
  }
  cached = resolved;
  return cached;
}
